import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clapperboard,
  Clock,
  Copy,
  Filter,
  Image as ImageIcon,
  Plus,
  Sparkles,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import {
  ContentFormat,
  ContentPlatform,
  ContentPost,
  ContentStatus,
} from '../types';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

interface SocialBoardProps {
  workspaceId: string;
  reloadToken?: number;
}

const COLUMNS: { status: ContentStatus; label: string; hint: string }[] = [
  { status: ContentStatus.IDEA, label: 'Idea', hint: 'Gancho o tema' },
  { status: ContentStatus.DRAFTING, label: 'Borrador', hint: 'Copy en marcha' },
  { status: ContentStatus.REVIEW, label: 'Revisión', hint: 'Listo para mirar' },
  { status: ContentStatus.SCHEDULED, label: 'Programado', hint: 'Fecha cerrada' },
  { status: ContentStatus.PUBLISHED, label: 'Publicado', hint: 'Ya salió' },
];

const PLATFORM_LABELS: Record<ContentPlatform, string> = {
  [ContentPlatform.LINKEDIN]: 'LinkedIn',
  [ContentPlatform.INSTAGRAM]: 'Instagram',
  [ContentPlatform.TIKTOK]: 'TikTok',
  [ContentPlatform.YOUTUBE]: 'YouTube',
  [ContentPlatform.TWITTER]: 'X',
  [ContentPlatform.BLOG]: 'Blog',
  [ContentPlatform.NEWSLETTER]: 'Newsletter',
};

const FORMAT_LABELS: Record<ContentFormat, string> = {
  [ContentFormat.TEXT]: 'Texto',
  [ContentFormat.IMAGE]: 'Imagen',
  [ContentFormat.VIDEO]: 'Vídeo',
  [ContentFormat.CAROUSEL]: 'Carrusel',
  [ContentFormat.ARTICLE]: 'Artículo',
};

const PRESET_WARNINGS = [
  '⚠️ Falta editar',
  '⚡ Falta parte de IA',
  '🎨 Falta diseño / vídeo',
  '👀 Pendiente revisión',
  '✅ Listo para programar',
];

const LOCAL_KEY = 'sdai_content_posts';

const toPlatforms = (row: any): ContentPlatform[] => {
  if (Array.isArray(row.platforms)) {
    return row.platforms.filter(Boolean) as ContentPlatform[];
  }
  const raw = String(row.platform ?? '');
  const parsed = raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean) as ContentPlatform[];
  return parsed.length ? parsed : [ContentPlatform.LINKEDIN];
};

const mapPost = (row: any): ContentPost => ({
  id: row.id,
  workspaceId: row.workspace_id ?? '',
  title: row.title ?? 'Sin título',
  platforms: toPlatforms(row),
  format: (row.format as ContentFormat) || ContentFormat.TEXT,
  status: (row.status as ContentStatus) || ContentStatus.IDEA,
  scheduledFor: row.scheduled_for ?? undefined,
  content: row.content ?? undefined,
  script: row.script ?? undefined,
  mediaUrl: row.media_url ?? undefined,
  ideaId: row.idea_id ?? undefined,
  notes: row.notes ?? undefined,
});

const emptyDraft = (status: ContentStatus): ContentPost => ({
  id: crypto.randomUUID(),
  workspaceId: '',
  title: '',
  platforms: [ContentPlatform.LINKEDIN],
  format: ContentFormat.TEXT,
  status,
  content: '',
  scheduledFor: '',
  mediaUrl: '',
  notes: '',
});

const formatDate = (date?: string) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short' }).format(parsed);
};

const getDateStatus = (scheduledFor?: string, status?: ContentStatus) => {
  if (!scheduledFor) return null;
  const date = new Date(scheduledFor);
  if (Number.isNaN(date.getTime())) return null;

  if (status === ContentStatus.PUBLISHED) {
    return { label: formatDate(scheduledFor), color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25' };
  }

  const now = new Date();
  const isPast = date.getTime() < now.getTime();
  const isToday = date.toDateString() === now.toDateString();

  if (isPast) {
    return { label: `Atrasada · ${formatDate(scheduledFor)}`, color: 'text-rose-400 bg-rose-500/10 border-rose-500/25' };
  }
  if (isToday) {
    const timeStr = date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return { label: `Hoy ${timeStr}`, color: 'text-[#FF9800] bg-[#FF9800]/10 border-[#FF9800]/30' };
  }
  return { label: formatDate(scheduledFor), color: 'text-[#f5f2ec]/65 bg-white/5 border-white/10' };
};

const startOfWeek = (date: Date) => {
  const copy = new Date(date);
  const day = (copy.getDay() + 6) % 7;
  copy.setHours(0, 0, 0, 0);
  copy.setDate(copy.getDate() - day);
  return copy;
};

const localDateKey = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

const addDays = (date: Date, days: number) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const formatWeekRange = (start: Date) => {
  const end = addDays(start, 6);
  const fmt = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short' });
  const sameMonth = start.getMonth() === end.getMonth();
  if (sameMonth) {
    const s = new Intl.DateTimeFormat('es-ES', { day: 'numeric' }).format(start);
    return `${s} – ${fmt.format(end)}`;
  }
  return `${fmt.format(start)} – ${fmt.format(end)}`;
};

/** Copia texto al portapapeles con fallback para iOS / contextos no seguros. */
const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Cae al fallback manual de abajo.
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

/** Cuenta palabras de un texto (para el indicador del guion). */
const wordCount = (text?: string): number => {
  const t = (text ?? '').trim();
  return t ? t.split(/\s+/).length : 0;
};

export const SocialBoard: React.FC<SocialBoardProps> = ({ workspaceId, reloadToken = 0 }) => {
  const [posts, setPosts] = useState<ContentPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState<'ALL' | ContentPlatform>('ALL');
  const [onlyWarningsFilter, setOnlyWarningsFilter] = useState(false);
  const [mobileColumn, setMobileColumn] = useState<'ALL' | ContentStatus>('ALL');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overStatus, setOverStatus] = useState<ContentStatus | null>(null);
  const [editor, setEditor] = useState<ContentPost | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoPublishedNotice, setAutoPublishedNotice] = useState<string | null>(null);
  // Calendario interactivo: offset en semanas (0 = semana actual) + día seleccionado (móvil).
  // Patrón Buffer/Later: Week view navegable + tap en día para detalle + CTA crear.
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDayKey, setSelectedDayKey] = useState<string>(() => localDateKey(new Date()));
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  // Feedback del copiado directo: clave "postId:campo" (content o script) + temporizador.
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copiedTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copiedTimer.current != null) window.clearTimeout(copiedTimer.current);
    },
    []
  );

  const handleCopyText = async (text: string | undefined, key: string, event?: React.SyntheticEvent) => {
    event?.stopPropagation();
    const content = (text ?? '').trim();
    if (!content) return;
    const ok = await copyTextToClipboard(content);
    if (ok) {
      setCopiedKey(key);
      if (copiedTimer.current != null) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopiedKey(null), 2000);
    } else {
      setError('No se pudo copiar automáticamente. Ábrelo en el editor y cópialo manualmente.');
    }
  };

  const handleCopyPost = (post: ContentPost, event?: React.SyntheticEvent) =>
    void handleCopyText(post.content, `${post.id}:content`, event);

  const persistLocal = (next: ContentPost[]) => {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
  };

  /**
   * Automatización: Revisa si hay posts en SCHEDULED cuya fecha/hora se haya cumplido.
   * Si es así, los pasa a PUBLISHED y sincroniza en Supabase y localmente.
   */
  const checkAndAutoPublish = async (currentPosts: ContentPost[]): Promise<ContentPost[]> => {
    const now = Date.now();
    const duePosts = currentPosts.filter(
      (post) =>
        post.status === ContentStatus.SCHEDULED &&
        post.scheduledFor &&
        !Number.isNaN(new Date(post.scheduledFor).getTime()) &&
        new Date(post.scheduledFor).getTime() <= now
    );

    if (duePosts.length === 0) return currentPosts;

    const dueIds = new Set(duePosts.map((p) => p.id));
    const nextPosts = currentPosts.map((post) =>
      dueIds.has(post.id) ? { ...post, status: ContentStatus.PUBLISHED } : post
    );

    persistLocal(nextPosts);

    if (supabase) {
      for (const duePost of duePosts) {
        try {
          await supabase
            .from('content_posts')
            .update({ status: ContentStatus.PUBLISHED })
            .eq('id', duePost.id);
        } catch (err) {
          console.error('Error auto-publishing post:', err);
        }
      }
    }

    setAutoPublishedNotice(
      `🔔 ${duePosts.length} publicación(es) cuya fecha venció pasaron a 'Publicado'.`
    );

    return nextPosts;
  };

  const loadPosts = async () => {
    setLoading(true);
    setError(null);

    let fetched: ContentPost[] = [];

    if (supabase) {
      const { data, error: queryError } = await supabase
        .from('content_posts')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('scheduled_for', { ascending: true });

      if (!queryError && data) {
        fetched = data.map(mapPost);
      } else if (queryError) {
        console.warn('Lectura de content_posts en Supabase:', queryError.message);
      }
    }

    // Fusionar con datos locales para que no se pierdan si las columnas aún no existen en Supabase
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      const localParsed = raw ? (JSON.parse(raw) as ContentPost[]) : [];
      if (Array.isArray(localParsed) && localParsed.length > 0) {
        if (fetched.length === 0) {
          fetched = localParsed;
        } else {
          const localMap = new Map(localParsed.map((p) => [p.id, p]));
          fetched = fetched.map((p) => {
            const local = localMap.get(p.id);
            return {
              ...p,
              notes: p.notes ?? local?.notes,
              script: p.script ?? local?.script,
            };
          });
        }
      }
    } catch {
      // Ignorar errores de lectura local
    }

    const updated = await checkAndAutoPublish(fetched);
    setPosts(updated);
    setLoading(false);
  };

  useEffect(() => {
    void loadPosts();
  }, [workspaceId, reloadToken]);

  // Intervalo que evalúa publicaciones periódicamente (cada 30 segundos)
  useEffect(() => {
    const timer = setInterval(() => {
      setPosts((current) => {
        void checkAndAutoPublish(current).then((evaluated) => {
          const changed = evaluated.some(
            (p, idx) => p.status !== current[idx]?.status
          );
          if (changed) {
            setPosts(evaluated);
          }
        });
        return current;
      });
    }, 30000);

    return () => clearInterval(timer);
  }, [workspaceId]);

  const visiblePosts = useMemo(
    () =>
      posts.filter((post) => {
        const matchesPlatform =
          platformFilter === 'ALL' || post.platforms.includes(platformFilter);
        const matchesWarning = !onlyWarningsFilter || Boolean(post.notes && post.notes.trim());
        return matchesPlatform && matchesWarning;
      }),
    [onlyWarningsFilter, platformFilter, posts]
  );

  const weekStart = useMemo(() => addDays(startOfWeek(new Date()), weekOffset * 7), [weekOffset]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const todayKey = useMemo(() => localDateKey(new Date()), []);

  const weekDays = useMemo(() => {
    const start = weekStart;
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      const key = localDateKey(date);
      return {
        key,
        fullDate: date,
        label: new Intl.DateTimeFormat('es-ES', { weekday: 'short' }).format(date),
        day: date.getDate(),
        isToday: key === todayKey,
        isSelected: key === selectedDayKey,
        items: visiblePosts.filter((post) => post.scheduledFor?.slice(0, 10) === key),
      };
    });
  }, [visiblePosts, weekStart, todayKey, selectedDayKey]);

  const selectedDay = useMemo(
    () => weekDays.find((d) => d.key === selectedDayKey) ?? weekDays.find((d) => d.isToday) ?? weekDays[0],
    [weekDays, selectedDayKey]
  );

  const counts = useMemo(
    () => ({
      total: posts.length,
      scheduled: posts.filter((post) => post.status === ContentStatus.SCHEDULED).length,
      published: posts.filter((post) => post.status === ContentStatus.PUBLISHED).length,
      withWarnings: posts.filter((post) => post.notes && post.notes.trim()).length,
    }),
    [posts]
  );

  const writePost = async (post: ContentPost) => {
    if (!supabase) return;

    const payload = {
      id: post.id,
      workspace_id: workspaceId,
      title: post.title.trim() || 'Sin título',
      platform: post.platforms.join(','),
      format: post.format,
      status: post.status,
      content: post.content || null,
      script: post.script || null,
      media_url: post.mediaUrl || null,
      scheduled_for: post.scheduledFor || null,
      notes: post.notes || null,
    };

    try {
      const { error: writeError } = await supabase.from('content_posts').upsert(payload);
      if (writeError) {
        const msg = (writeError.message || '').toLowerCase();
        // Si las columnas 'notes'/'script' no existen aún en Supabase, reintentar sin ellas.
        // El guion sigue guardado en localStorage hasta aplicar supabase_content_guion.sql.
        if (msg.includes('notes') || msg.includes('script') || writeError.code === 'PGRST204' || writeError.code === '42703') {
          console.warn('Columnas notes/script no existen en Supabase. Guardando sin esos campos en Supabase...');
          const { notes, script, ...withoutExtra } = payload;
          const { error: retryError } = await supabase.from('content_posts').upsert(withoutExtra);
          if (retryError) throw retryError;
          return;
        }
        throw writeError;
      }
    } catch (err: any) {
      console.warn('Error al guardar en Supabase (guardado localmente):', err?.message);
      // No bloquear la interfaz si Supabase tiene un problema de esquema o RLS, los datos ya están en localStorage
    }
  };

  const upsertLocal = (post: ContentPost) => {
    setPosts((current) => {
      const exists = current.some((item) => item.id === post.id);
      const next = exists
        ? current.map((item) => (item.id === post.id ? post : item))
        : [...current, post];
      persistLocal(next);
      return next;
    });
  };

  const movePost = async (id: string, status: ContentStatus) => {
    const current = posts.find((post) => post.id === id);
    if (!current || current.status === status) return;
    const next = { ...current, status };
    upsertLocal(next);
    try {
      await writePost(next);
    } catch (moveError) {
      setError(moveError instanceof Error ? moveError.message : 'No se pudo mover la pieza.');
      void loadPosts();
    }
  };

  const movePostStep = (post: ContentPost, direction: -1 | 1) => {
    const currentIndex = COLUMNS.findIndex((c) => c.status === post.status);
    const targetCol = COLUMNS[currentIndex + direction];
    if (targetCol) {
      void movePost(post.id, targetCol.status);
    }
  };

  const saveEditor = async () => {
    if (!editor) return;
    if (!editor.title.trim()) {
      setError('Pon un título a la pieza.');
      return;
    }
    setSaving(true);
    setError(null);
    const next = { ...editor, workspaceId };
    upsertLocal(next);
    try {
      await writePost(next);
      setEditor(null);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'No se pudo guardar en Supabase.');
    }
    setSaving(false);
  };

  const deletePost = async (id: string) => {
    setPosts((current) => {
      const next = current.filter((post) => post.id !== id);
      persistLocal(next);
      return next;
    });
    setEditor(null);
    if (supabase) {
      const { error: deleteError } = await supabase.from('content_posts').delete().eq('id', id);
      if (deleteError) setError(deleteError.message);
    }
  };

  const togglePlatform = (platform: ContentPlatform) => {
    if (!editor) return;
    const already = editor.platforms.includes(platform);
    const platforms = already
      ? editor.platforms.filter((item) => item !== platform)
      : [...editor.platforms, platform];
    setEditor({ ...editor, platforms: platforms.length ? platforms : [platform] });
  };

  const toggleWarningPreset = (presetText: string) => {
    if (!editor) return;
    const currentNotes = editor.notes ?? '';
    if (currentNotes.includes(presetText)) {
      const cleaned = currentNotes
        .replace(presetText, '')
        .replace(/,\s*,/g, ',')
        .trim();
      setEditor({ ...editor, notes: cleaned });
    } else {
      const nextNotes = currentNotes.trim() ? `${currentNotes.trim()}, ${presetText}` : presetText;
      setEditor({ ...editor, notes: nextNotes });
    }
  };

  const copyStats = useMemo(() => {
    const text = editor?.content ?? '';
    const charCount = text.length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    return { charCount, words };
  }, [editor?.content]);

  const columnsToRender = useMemo(() => {
    return COLUMNS.filter((col) => mobileColumn === 'ALL' || col.status === mobileColumn);
  }, [mobileColumn]);

  return (
    <section className="space-y-4 sm:space-y-6">
      {/* Encabezado de Redes */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-[#f5f2ec] sm:text-3xl">Tablero de Redes</h2>
          <p className="mt-1 text-xs leading-relaxed text-[#f5f2ec]/60 sm:text-sm">
            Planifica, redacta y gestiona el flujo de publicaciones para LinkedIn, Instagram y demás canales.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditor(emptyDraft(ContentStatus.IDEA))}
          className="inline-flex h-10 sm:h-11 items-center justify-center gap-2 rounded-full bg-[#FF9800] px-4 sm:px-5 text-xs sm:text-sm font-bold text-black shadow-md shadow-[#FF9800]/20 transition-all hover:bg-[#FF9800]/90 active:scale-95 shrink-0"
        >
          <Plus className="h-4 w-4" />
          Nueva pieza
        </button>
      </div>

      {/* Métricas rápidas: en móvil 2x2 para no comer pantalla */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 sm:grid-cols-4">
        <MiniMetric label="En tablero" value={String(counts.total)} />
        <MiniMetric label="Programadas" value={String(counts.scheduled)} icon={Clock} />
        <MiniMetric label="Publicadas" value={String(counts.published)} icon={CheckCircle2} />
        <MiniMetric
          label="Con avisos"
          value={String(counts.withWarnings)}
          icon={AlertCircle}
          highlight={counts.withWarnings > 0}
        />
      </div>

      {/* Barra de Filtros en Píldora adaptada a móvil con scroll horizontal */}
      <div className="flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.025] p-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex max-w-full gap-1.5 overflow-x-auto no-scrollbar">
          {([['ALL', 'Todas'], ...Object.entries(PLATFORM_LABELS)] as [string, string][]).map(
            ([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setPlatformFilter(value as 'ALL' | ContentPlatform)}
                className={`h-7 sm:h-8 shrink-0 rounded-full px-3 text-[11px] sm:text-xs font-medium transition ${
                  platformFilter === value
                    ? 'bg-[#f5f2ec] text-black font-semibold shadow-sm'
                    : 'text-[#f5f2ec]/60 hover:bg-white/[0.06] hover:text-[#f5f2ec]'
                }`}
              >
                {label}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          onClick={() => setOnlyWarningsFilter(!onlyWarningsFilter)}
          className={`flex h-7 sm:h-8 items-center justify-center gap-1.5 rounded-full px-3 text-[11px] sm:text-xs font-medium transition border shrink-0 ${
            onlyWarningsFilter
              ? 'border-amber-500/50 bg-amber-500/20 text-amber-300 font-semibold'
              : 'border-white/10 text-[#f5f2ec]/60 hover:bg-white/[0.05] hover:text-[#f5f2ec]'
          }`}
        >
          <AlertCircle className="h-3.5 w-3.5 text-amber-400" />
          <span>Solo con avisos ({counts.withWarnings})</span>
        </button>
      </div>

      {/* Calendario semanal interactivo: navegable por semanas, táctil, crear por día */}
      <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-white/[0.02] p-3 sm:p-4">
        <div className="mb-2.5 flex items-center justify-between gap-2">
          <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-[#FF9800]">
            Calendario semanal
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              aria-label="Semana anterior"
              onClick={() => setWeekOffset((w) => w - 1)}
              className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.03] text-[#f5f2ec]/70 transition active:scale-95 hover:border-[#FF9800]/60 hover:text-[#FF9800]"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => {
                setWeekOffset(0);
                setSelectedDayKey(localDateKey(new Date()));
              }}
              className={`h-11 rounded-full px-4 text-xs font-bold transition active:scale-95 ${
                weekOffset === 0
                  ? 'bg-[#f5f2ec] text-black'
                  : 'border border-white/10 bg-white/[0.03] text-[#f5f2ec]/70 hover:text-[#f5f2ec]'
              }`}
            >
              Hoy
            </button>
            <button
              type="button"
              aria-label="Semana siguiente"
              onClick={() => setWeekOffset((w) => w + 1)}
              className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.03] text-[#f5f2ec]/70 transition active:scale-95 hover:border-[#FF9800]/60 hover:text-[#FF9800]"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="mb-2.5 flex items-center justify-between">
          <p className="text-sm sm:text-base font-bold text-[#f5f2ec] capitalize">
            {formatWeekRange(weekStart)}
            {weekOffset === 0 && (
              <span className="ml-2 rounded-full bg-[#FF9800]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#FF9800]">
                Esta semana
              </span>
            )}
          </p>
          <span className="text-[10px] text-[#f5f2ec]/40 sm:hidden">Toca un día · desliza ↔ para cambiar semana</span>
        </div>
        <div
          className="flex gap-2 overflow-x-auto no-scrollbar pb-1 sm:grid sm:grid-cols-4 lg:grid-cols-7"
          onTouchStart={(e) => setTouchStartX(e.touches[0].clientX)}
          onTouchEnd={(e) => {
            if (touchStartX == null) return;
            const dx = e.changedTouches[0].clientX - touchStartX;
            if (Math.abs(dx) > 60) setWeekOffset((w) => w + (dx < 0 ? 1 : -1));
            setTouchStartX(null);
          }}
        >
          {weekDays.map((day) => (
            <div
              key={day.key}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedDayKey(day.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setSelectedDayKey(day.key);
              }}
              className={`flex min-w-[110px] sm:min-w-0 flex-1 cursor-pointer flex-col justify-between rounded-xl sm:rounded-2xl border p-2 sm:p-2.5 transition active:scale-[0.99] ${
                day.isSelected
                  ? 'border-[#FF9800] bg-[#FF9800]/[0.08] ring-1 ring-[#FF9800]/40'
                  : day.isToday
                    ? 'border-[#FF9800]/40 bg-black/30'
                    : 'border-white/5 bg-black/30 hover:border-white/15'
              }`}
            >
              <div className="flex items-center justify-between text-xs">
                <span className={`font-semibold uppercase tracking-wider ${day.isSelected ? 'text-[#FF9800]' : 'text-[#f5f2ec]/50'}`}>{day.label}</span>
                <span className={`rounded-full px-1.5 py-0.2 text-[10px] font-bold ${day.isToday ? 'bg-[#FF9800] text-black' : 'bg-white/[0.06] text-[#f5f2ec]'}`}>
                  {day.day}
                </span>
              </div>
              <div className="mt-1.5 space-y-1">
                {day.items.slice(0, 2).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditor(item);
                    }}
                    className="block min-h-[32px] w-full truncate rounded-md bg-[#FF9800]/15 px-1.5 py-1 text-left text-[10px] sm:text-[11px] font-medium text-[#FF9800] hover:bg-[#FF9800]/25 transition"
                    title={item.title}
                  >
                    {item.title}
                  </button>
                ))}
                {day.items.length > 2 && (
                  <span className="block text-[9px] text-[#f5f2ec]/40">+{day.items.length - 2} más</span>
                )}
                {day.items.length === 0 && (
                  <span className="block text-[9px] text-[#f5f2ec]/20 py-1">Sin posts</span>
                )}
                <button
                  type="button"
                  aria-label={`Crear pieza el ${day.key}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDayKey(day.key);
                    const draft = emptyDraft(ContentStatus.SCHEDULED);
                    draft.scheduledFor = `${day.key}T10:00`;
                    setEditor(draft);
                  }}
                  className="mt-1 grid h-8 w-full place-items-center rounded-lg border border-dashed border-white/15 text-[#f5f2ec]/40 transition hover:border-[#FF9800]/60 hover:text-[#FF9800]"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
        {/* Detalle del día seleccionado: clave en móvil para operar todo con el pulgar */}
        {selectedDay && (
          <div className="mt-3 rounded-2xl border border-white/10 bg-black/30 p-3 sm:p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs sm:text-sm font-bold text-[#f5f2ec] capitalize">
                {new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'short' }).format(selectedDay.fullDate)}
                <span className="ml-2 font-medium text-[#f5f2ec]/50">· {selectedDay.items.length} piezas</span>
              </p>
              <button
                type="button"
                onClick={() => {
                  const draft = emptyDraft(ContentStatus.SCHEDULED);
                  draft.scheduledFor = `${selectedDay.key}T10:00`;
                  setEditor(draft);
                }}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-[#FF9800] px-4 text-xs font-bold text-black transition active:scale-95"
              >
                <Plus className="h-4 w-4" />
                Añadir aquí
              </button>
            </div>
            {selectedDay.items.length === 0 ? (
              <p className="py-2 text-center text-xs text-[#f5f2ec]/40">Nada programado este día. Toca “Añadir aquí”.</p>
            ) : (
              <div className="space-y-2">
                {selectedDay.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setEditor(item)}
                    className="flex min-h-[52px] w-full items-center justify-between gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5 text-left transition hover:border-[#FF9800]/50 active:scale-[0.99]"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-[#f5f2ec]">{item.title}</span>
                      <span className="block text-[11px] text-[#f5f2ec]/50">
                        {(item.scheduledFor?.slice(11, 16) || 'Sin hora')} · {item.platforms.join(', ')} · {item.status}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-[#f5f2ec]/40" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Aviso de publicación automática si ha ocurrido */}
      {autoPublishedNotice && (
        <div className="flex items-center justify-between rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs sm:text-sm text-emerald-300">
          <span>{autoPublishedNotice}</span>
          <button
            type="button"
            onClick={() => setAutoPublishedNotice(null)}
            className="rounded-full p-1 hover:bg-emerald-500/20 text-emerald-300"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs sm:text-sm text-red-300">
          {error}
        </div>
      )}

      {/* SELECTOR DE COLUMNAS EN MÓVIL (TIPO PÍLDORAS DESLIZABLES) */}
      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1 lg:hidden">
        <button
          type="button"
          onClick={() => setMobileColumn('ALL')}
          className={`h-8 rounded-full px-3.5 text-xs font-semibold shrink-0 transition ${
            mobileColumn === 'ALL'
              ? 'bg-[#FF9800] text-black shadow-sm'
              : 'border border-white/10 bg-white/[0.03] text-[#f5f2ec]/60'
          }`}
        >
          Ver tablero completo
        </button>
        {COLUMNS.map((col) => {
          const colCount = visiblePosts.filter((p) => p.status === col.status).length;
          return (
            <button
              key={col.status}
              type="button"
              onClick={() => setMobileColumn(col.status)}
              className={`flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold shrink-0 transition border ${
                mobileColumn === col.status
                  ? 'border-[#FF9800] bg-[#FF9800]/20 text-[#FF9800]'
                  : 'border-white/10 bg-white/[0.03] text-[#f5f2ec]/60'
              }`}
            >
              <span>{col.label}</span>
              <span className="rounded-full bg-white/10 px-1.5 py-0.2 text-[10px]">{colCount}</span>
            </button>
          );
        })}
      </div>

      {/* Columnas Kanban Estilo Trello / Google adaptado a móvil */}
      {loading ? (
        <div className="grid min-h-[35vh] place-items-center rounded-3xl border border-white/10 bg-white/[0.02] text-xs sm:text-sm text-[#f5f2ec]/50">
          Cargando piezas de contenido…
        </div>
      ) : (
        <div
          className={
            mobileColumn === 'ALL'
              ? '-mx-4 flex gap-3 overflow-x-auto snap-x snap-mandatory px-4 pb-4 no-scrollbar lg:mx-0 lg:grid lg:grid-cols-5 lg:overflow-visible lg:px-0'
              : 'w-full'
          }
        >
          {columnsToRender.map((column) => {
            const colIdx = COLUMNS.findIndex((c) => c.status === column.status);
            const items = visiblePosts.filter((post) => post.status === column.status);
            const isOver = overStatus === column.status;
            const isSingleMobileCol = mobileColumn !== 'ALL';

            return (
              <div
                key={column.status}
                onDragOver={(event) => {
                  event.preventDefault();
                  setOverStatus(column.status);
                }}
                onDragLeave={() => setOverStatus(null)}
                onDrop={(event) => {
                  event.preventDefault();
                  setOverStatus(null);
                  if (draggingId) void movePost(draggingId, column.status);
                  setDraggingId(null);
                }}
                className={`flex min-h-[420px] flex-col rounded-3xl border bg-white/[0.02] p-3 sm:p-3.5 transition-all ${
                  isSingleMobileCol ? 'w-full' : 'w-[85vw] max-w-[310px] shrink-0 snap-center lg:w-auto lg:max-w-none'
                } ${
                  isOver ? 'border-[#FF9800] bg-white/[0.04] ring-2 ring-[#FF9800]/20' : 'border-white/10'
                }`}
              >
                {/* Cabecera de columna */}
                <div className="mb-3 flex items-start justify-between px-1">
                  <div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-[#f5f2ec]">
                      {column.label}
                    </h3>
                    <p className="mt-0.5 text-xs text-[#f5f2ec]/40">{column.hint}</p>
                  </div>
                  <span className="rounded-full bg-white/[0.06] px-2.5 py-0.5 text-xs font-bold text-[#FF9800]">
                    {items.length}
                  </span>
                </div>

                {/* Botón rápido añadir a columna */}
                <button
                  type="button"
                  onClick={() => setEditor(emptyDraft(column.status))}
                  className="mb-3 flex min-h-[44px] items-center justify-center gap-1.5 rounded-2xl border border-dashed border-white/15 text-xs font-medium text-[#f5f2ec]/60 transition hover:border-[#FF9800]/60 hover:bg-[#FF9800]/5 hover:text-[#FF9800]"
                >
                  <Plus className="h-4 w-4" />
                  Añadir pieza
                </button>

                {/* Lista de tarjetas */}
                <div className="flex-1 space-y-2.5">
                  {items.map((post) => {
                    const dateStatus = getDateStatus(post.scheduledFor, post.status);
                    const prevCol = colIdx > 0 ? COLUMNS[colIdx - 1] : null;
                    const nextCol = colIdx < COLUMNS.length - 1 ? COLUMNS[colIdx + 1] : null;

                    return (
                      <article
                        key={post.id}
                        draggable
                        onDragStart={() => setDraggingId(post.id)}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setOverStatus(null);
                        }}
                        onClick={() => setEditor(post)}
                        className={`group cursor-pointer rounded-2xl border border-white/10 bg-[#121212]/95 p-3 text-left shadow-sm transition-all hover:border-[#FF9800]/60 hover:bg-[#161616] active:scale-[0.99] ${
                          draggingId === post.id ? 'opacity-30' : ''
                        }`}
                      >
                        {/* Título */}
                        <p className="text-sm font-semibold leading-snug text-[#f5f2ec]">
                          {post.title}
                        </p>

                        {/* Canales pills */}
                        <div className="mt-2 flex flex-wrap gap-1">
                          {post.platforms.map((platform) => (
                            <span
                              key={platform}
                              className="rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-[#f5f2ec]/70"
                            >
                              {PLATFORM_LABELS[platform]}
                            </span>
                          ))}
                          <span className="rounded-full border border-white/10 bg-white/[0.03] px-1.5 py-0.5 text-[9px] text-[#f5f2ec]/40">
                            {FORMAT_LABELS[post.format]}
                          </span>
                        </div>

                        {/* ADVERTENCIA VISUAL DESTACADA (Falta editar, Falta IA, etc.) */}
                        {post.notes && post.notes.trim() && (
                          <div className="mt-2 flex items-start gap-1.5 rounded-xl border border-amber-500/35 bg-amber-500/10 px-2 py-1 text-xs text-amber-300">
                            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-400" />
                            <span className="font-semibold leading-tight line-clamp-2">
                              {post.notes}
                            </span>
                          </div>
                        )}

                        {/* Fecha programada con status badge */}
                        {dateStatus && (
                          <div className="mt-2">
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium ${dateStatus.color}`}
                            >
                              <CalendarClock className="h-3 w-3" />
                              {dateStatus.label}
                            </span>
                          </div>
                        )}

                        {/* Apartado Copy con copiado directo (sin abrir el editor) */}
                        {post.content && post.content.trim() ? (
                          <div className="mt-2 rounded-xl border border-white/10 bg-black/30 p-2">
                            <p className="line-clamp-3 whitespace-pre-line text-[11px] leading-relaxed text-[#f5f2ec]/60">
                              {post.content.trim()}
                            </p>
                            <button
                              type="button"
                              onClick={(e) => void handleCopyPost(post, e)}
                              aria-label={`Copiar copy de ${post.title}`}
                              className={`mt-1.5 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border px-3 text-xs font-bold transition active:scale-95 ${
                                copiedKey === `${post.id}:content`
                                  ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300'
                                  : 'border-[#FF9800]/40 bg-[#FF9800]/10 text-[#FF9800] hover:bg-[#FF9800]/20'
                              }`}
                            >
                              {copiedKey === `${post.id}:content` ? (
                                <>
                                  <Check className="h-4 w-4" />
                                  ¡Copiado!
                                </>
                              ) : (
                                <>
                                  <Copy className="h-4 w-4" />
                                  Copiar copy
                                </>
                              )}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditor(post);
                            }}
                            className="mt-2 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-white/15 px-3 text-[11px] font-medium text-[#f5f2ec]/40 transition hover:border-[#FF9800]/60 hover:text-[#FF9800]"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Añadir copy
                          </button>
                        )}

                        {/* Apartado Guion: solo para reels / vídeos (formato VIDEO) */}
                        {post.format === ContentFormat.VIDEO &&
                          (post.script && post.script.trim() ? (
                            <div className="mt-2 rounded-xl border border-violet-500/30 bg-violet-500/10 p-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-violet-300">
                                  📝 Guion · {wordCount(post.script)} palabras
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => void handleCopyText(post.script, `${post.id}:script`, e)}
                                  aria-label={`Copiar guion de ${post.title}`}
                                  className={`grid h-11 w-11 shrink-0 place-items-center rounded-full border transition active:scale-95 ${
                                    copiedKey === `${post.id}:script`
                                      ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300'
                                      : 'border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25'
                                  }`}
                                >
                                  {copiedKey === `${post.id}:script` ? (
                                    <Check className="h-5 w-5" />
                                  ) : (
                                    <Copy className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                              <p className="mt-1 line-clamp-3 whitespace-pre-line text-[11px] leading-relaxed text-[#f5f2ec]/60">
                                {post.script.trim()}
                              </p>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditor(post);
                              }}
                              className="mt-2 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-violet-500/30 px-3 text-[11px] font-medium text-violet-300/70 transition hover:border-violet-400/60 hover:text-violet-200"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              Añadir guion
                            </button>
                          ))}

                        {/* Acciones de cambio de columna rápido: targets 44px para pulgar (ref. mobile UX 2026) */}
                        <div className="mt-2.5 flex items-center justify-between border-t border-white/5 pt-2 text-[10px] text-[#f5f2ec]/40">
                          <span className="text-[#f5f2ec]/30 group-hover:text-[#f5f2ec]/50">
                            Toca para editar
                          </span>
                          <div className="flex items-center gap-2">
                            {prevCol && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  movePostStep(post, -1);
                                }}
                                title={`Mover a ${prevCol.label}`}
                                aria-label={`Mover a ${prevCol.label}`}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-[#f5f2ec]/70 active:scale-95 transition hover:border-[#FF9800]/50 hover:text-[#FF9800]"
                              >
                                <ChevronLeft className="h-5 w-5" />
                              </button>
                            )}
                            {nextCol && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  movePostStep(post, 1);
                                }}
                                title={`Mover a ${nextCol.label}`}
                                aria-label={`Mover a ${nextCol.label}`}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#FF9800]/30 bg-[#FF9800]/10 text-[#FF9800] active:scale-95 transition hover:bg-[#FF9800]/20"
                              >
                                <ChevronRight className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                  {items.length === 0 && (
                    <div className="flex h-28 items-center justify-center rounded-2xl border border-dashed border-white/10 p-3 text-center text-xs text-[#f5f2ec]/30">
                      Suelta aquí una pieza
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Editor de Pieza: estilo Bottom-Sheet en móvil para excelente ergonomía táctil */}
      {editor && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end sm:justify-center bg-black/80 backdrop-blur-sm sm:p-4">
          <div className="flex max-h-[90vh] sm:max-h-[92vh] w-full max-w-2xl flex-col rounded-t-3xl sm:rounded-3xl border-t sm:border border-white/15 bg-[#111111] p-4 sm:p-6 shadow-2xl overflow-hidden pb-[max(1.25rem,env(safe-area-inset-bottom))] animate-in fade-in slide-in-from-bottom duration-200">
            {/* Tirador táctil visual en móvil */}
            <div className="mx-auto mb-2 h-1 w-12 rounded-full bg-white/20 sm:hidden" />

            {/* Header modal */}
            <div className="mb-4 flex items-start justify-between gap-3 border-b border-white/10 pb-3">
              <div>
                <span className="rounded-full bg-[#FF9800]/15 px-2.5 py-0.5 text-[10px] font-semibold text-[#FF9800]">
                  Pieza de contenido
                </span>
                <h3 className="mt-1 text-xl font-bold tracking-tight text-[#f5f2ec] sm:text-2xl">
                  {posts.some((post) => post.id === editor.id) ? 'Editar publicación' : 'Nueva publicación'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setEditor(null)}
                className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/[0.03] text-[#f5f2ec]/60 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Form scrollable */}
            <div className="flex-1 space-y-3.5 overflow-y-auto pr-1">
              {/* Título */}
              <Field label="Título / Hook principal">
                <input
                  value={editor.title}
                  onChange={(event) => setEditor({ ...editor, title: event.target.value })}
                  className="h-10 sm:h-11 w-full rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 px-3.5 text-sm text-[#f5f2ec] outline-none focus:border-[#FF9800]"
                  placeholder="Hook potente o título descriptivo..."
                />
              </Field>

              {/* SECCIÓN ADVERTENCIAS Y NOTAS RÁPIDAS */}
              <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-400">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Advertencias y Notas
                  </span>
                  <span className="text-[10px] text-[#f5f2ec]/40">Visible en la tarjeta</span>
                </div>

                {/* Chips preset de 1 clic */}
                <div className="mb-2 flex flex-wrap gap-1">
                  {PRESET_WARNINGS.map((preset) => {
                    const isActive = (editor.notes ?? '').includes(preset);
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => toggleWarningPreset(preset)}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition border ${
                          isActive
                            ? 'border-amber-400 bg-amber-500/30 text-amber-200 font-semibold'
                            : 'border-white/10 bg-black/30 text-[#f5f2ec]/60 hover:border-amber-500/40 hover:text-amber-300'
                        }`}
                      >
                        {preset}
                      </button>
                    );
                  })}
                </div>

                <input
                  value={editor.notes ?? ''}
                  onChange={(event) => setEditor({ ...editor, notes: event.target.value })}
                  className="h-9 sm:h-10 w-full rounded-xl border border-white/10 bg-black/50 px-3 text-xs text-[#f5f2ec] placeholder-[#f5f2ec]/30 outline-none focus:border-amber-400"
                  placeholder="Escribe una advertencia personalizada..."
                />
              </div>

              {/* Canales en píldoras */}
              <Field label="Canales de publicación">
                <div className="flex flex-wrap gap-1.5">
                  {(Object.keys(PLATFORM_LABELS) as ContentPlatform[]).map((platform) => {
                    const isSelected = editor.platforms.includes(platform);
                    return (
                      <button
                        key={platform}
                        type="button"
                        onClick={() => togglePlatform(platform)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                          isSelected
                            ? 'bg-[#FF9800] text-black shadow-sm'
                            : 'border border-white/10 bg-black/30 text-[#f5f2ec]/50 hover:text-[#f5f2ec]'
                        }`}
                      >
                        {PLATFORM_LABELS[platform]}
                      </button>
                    );
                  })}
                </div>
              </Field>

              {/* Copy con contador de caracteres y copiado directo */}
              <Field label="Texto del Copy">
                <div className="space-y-1">
                  <textarea
                    value={editor.content ?? ''}
                    onChange={(event) => setEditor({ ...editor, content: event.target.value })}
                    rows={5}
                    className="w-full rounded-2xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm leading-relaxed text-[#f5f2ec] outline-none focus:border-[#FF9800]"
                    placeholder="Escribe el copy de la publicación..."
                  />
                  <div className="flex items-center justify-between gap-2 text-[11px] text-[#f5f2ec]/40 px-1">
                    <span>{copyStats.words} palabras · {copyStats.charCount} caracteres</span>
                    <button
                      type="button"
                      onClick={() => void handleCopyPost(editor)}
                      disabled={!(editor.content ?? '').trim()}
                      className={`inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border px-4 text-xs font-bold transition active:scale-95 disabled:opacity-40 ${
                        copiedKey === `${editor.id}:content`
                          ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300'
                          : 'border-[#FF9800]/40 bg-[#FF9800]/10 text-[#FF9800] hover:bg-[#FF9800]/20'
                      }`}
                    >
                      {copiedKey === `${editor.id}:content` ? (
                        <>
                          <Check className="h-4 w-4" />
                          ¡Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copiar copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </Field>

              {/* Guion del reel / vídeo: solo aparece con formato Vídeo */}
              {editor.format === ContentFormat.VIDEO && (
                <div className="rounded-2xl border border-violet-500/25 bg-violet-500/[0.04] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-violet-300">
                      📝 Guion del reel / vídeo
                    </span>
                    <button
                      type="button"
                      onClick={() => void handleCopyText(editor.script, `${editor.id}:script`)}
                      disabled={!(editor.script ?? '').trim()}
                      className={`inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full border px-4 text-xs font-bold transition active:scale-95 disabled:opacity-40 ${
                        copiedKey === `${editor.id}:script`
                          ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300'
                          : 'border-violet-500/40 bg-violet-500/15 text-violet-200 hover:bg-violet-500/25'
                      }`}
                    >
                      {copiedKey === `${editor.id}:script` ? (
                        <>
                          <Check className="h-4 w-4" />
                          ¡Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copiar guion
                        </>
                      )}
                    </button>
                  </div>
                  <textarea
                    value={editor.script ?? ''}
                    onChange={(event) => setEditor({ ...editor, script: event.target.value })}
                    rows={6}
                    className="w-full rounded-2xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-sm leading-relaxed text-[#f5f2ec] outline-none placeholder:text-[#f5f2ec]/30 focus:border-violet-400"
                    placeholder={'Hook (0-3 s): frase que retiene…\nDesarrollo: 2-3 ideas, una por frase…\nCTA: qué debe hacer quien lo ve…'}
                  />
                  <div className="mt-1 flex items-center justify-between px-1 text-[11px] text-[#f5f2ec]/40">
                    <span>{wordCount(editor.script)} palabras</span>
                    <span>{(editor.script ?? '').length} caracteres</span>
                  </div>
                </div>
              )}

              {/* Formato y Estado */}              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Formato">
                  <select
                    value={editor.format}
                    onChange={(event) =>
                      setEditor({ ...editor, format: event.target.value as ContentFormat })
                    }
                    className="h-10 sm:h-11 w-full rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 px-3 text-sm text-[#f5f2ec] outline-none"
                  >
                    {(Object.keys(FORMAT_LABELS) as ContentFormat[]).map((format) => (
                      <option key={format} value={format} className="bg-[#1a1a1a]">
                        {FORMAT_LABELS[format]}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Estado">
                  <select
                    value={editor.status}
                    onChange={(event) =>
                      setEditor({ ...editor, status: event.target.value as ContentStatus })
                    }
                    className="h-10 sm:h-11 w-full rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 px-3 text-sm text-[#f5f2ec] outline-none"
                  >
                    {COLUMNS.map((column) => (
                      <option key={column.status} value={column.status} className="bg-[#1a1a1a]">
                        {column.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              {/* Fecha programada y Media URL */}
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Fecha y hora de publicación">
                  <input
                    type="datetime-local"
                    value={editor.scheduledFor ? editor.scheduledFor.slice(0, 16) : ''}
                    onChange={(event) => setEditor({ ...editor, scheduledFor: event.target.value })}
                    className="h-10 sm:h-11 w-full rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 px-3 text-sm text-[#f5f2ec] outline-none focus:border-[#FF9800]"
                  />
                </Field>

                <Field label="Enlace al material (Drive, vídeo, diseño)">
                  <input
                    value={editor.mediaUrl ?? ''}
                    onChange={(event) => setEditor({ ...editor, mediaUrl: event.target.value })}
                    className="h-10 sm:h-11 w-full rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 px-3 text-sm text-[#f5f2ec] outline-none focus:border-[#FF9800]"
                    placeholder="https://..."
                  />
                </Field>
              </div>
            </div>

            {/* Footer modal sticky */}
            <div className="mt-4 flex items-center justify-between gap-2 border-t border-white/10 pt-3">
              {posts.some((post) => post.id === editor.id) ? (
                <button
                  type="button"
                  onClick={() => void deletePost(editor.id)}
                  className="inline-flex h-10 items-center gap-1.5 rounded-full border border-red-500/30 px-3.5 text-xs font-semibold text-red-300 hover:bg-red-500/10 active:scale-95 transition"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Borrar
                </button>
              ) : (
                <span />
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEditor(null)}
                  className="h-10 rounded-full border border-white/10 px-4 text-xs font-semibold text-[#f5f2ec]/60 active:scale-95"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void saveEditor()}
                  disabled={saving}
                  className="inline-flex h-10 items-center gap-2 rounded-full bg-[#FF9800] px-5 text-xs sm:text-sm font-bold text-black shadow-md shadow-[#FF9800]/20 hover:bg-[#FF9800]/90 disabled:opacity-60 active:scale-95 transition"
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">
      {label}
    </span>
    {children}
  </label>
);

const MiniMetric: React.FC<{
  label: string;
  value: string;
  icon?: React.ElementType;
  highlight?: boolean;
}> = ({ label, value, icon: Icon = Clapperboard, highlight }) => (
  <div
    className={`rounded-2xl sm:rounded-3xl border p-3 sm:p-4 transition ${
      highlight
        ? 'border-amber-500/40 bg-amber-500/[0.08]'
        : 'border-white/10 bg-white/[0.025]'
    }`}
  >
    <div className="mb-2 sm:mb-3 flex items-center justify-between text-[#f5f2ec]/50">
      <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider">{label}</span>
      <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${highlight ? 'text-amber-400' : 'text-[#FF9800]'}`} />
    </div>
    <div className={`text-2xl sm:text-3xl font-bold tracking-tight ${highlight ? 'text-amber-300' : 'text-[#f5f2ec]'}`}>
      {value}
    </div>
  </div>
);
